import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { SubSink } from 'subsink2';
import { MatTabGroup } from '@angular/material/tabs';
import { AppLoginContainerModelUpdate, app_login_principal_RowClass, app_mst_link_subject_to_grp_RowClass, LinkSubjectToTeamContainerModel, LinkSubjectToTeamService, Pagination, PagingResponseModel, TenantUserProfileService } from 'blg-akaun-ts-lib';
import { Observable, of, zip } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { ViewColumnComponent } from '../../../../../../../view-column.component';
import { PaginationComponent } from '../../../../../../../utilities/pagination/pagination.component';
import { AppConfig } from '../../../../../../../visa';
import { UserModel, UserTeamModel } from '../../../../../../../models/team-permissions.model';
import { gridOptionsServerSide, pageFiltering, pageSorting, serverSideClear } from '../../../../../../../listing.utils';
import { PermissionFacade } from '../../../../../../../modules/permission/facades/permission.facade';
import { TeamPermStates } from '../../../../../../../modules/permission/team-permissions-controller/states';
import { TeamPermSelectors } from '../../../../../../../modules/permission/team-permissions-controller/selectors';
import { TeamPermActions } from '../../../../../../../modules/permission/team-permissions-controller/actions';
import { PermissionStates } from '../../../../../permission-controller/root';

interface LocalState {
  deactivateReturn: boolean;
  selectedIndex;
}

interface User {
  viewValue: string,
  obj: app_login_principal_RowClass
}

@Component({
  selector: 'app-team-members-add',
  templateUrl: './team-members-add.component.html',
  styleUrls: ['./team-members-add.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore]
})
export class TeamMembersAddComponent extends ViewColumnComponent {

  @ViewChild(PaginationComponent, {static: false})
  private paginationComponent: PaginationComponent;
  @ViewChild(MatTabGroup, {static: true}) matTab: MatTabGroup;

  protected readonly index = 2;
  private localState: LocalState;

  // initialise local states
  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateReturn$ = this.componentStore.select(state => state.localState.deactivateReturn);
  readonly selectedIndex$ = this.componentStore.select(state => (state.localState.selectedIndex));

  // previous index
  prevIndex: number;
  private prevLocalState: any;

  // api visa
  apiVisa = AppConfig.apiVisa;

  // subSink is to ensure all subscribe calls are unsubscribed to once the component is destroyed
  private subSink = new SubSink;

  // fields that require options
  // status
  rank: string;
  rankArr: any = ['OWNER', 'MANAGER', 'ADMIN', 'MEMBER', 'GUEST', 'VISITOR', 'ANONYMOUS'];
  newRankArr: any = [];

  public form: FormGroup;
  formDisabled: boolean = true;

  teamGuid: string;
  currentUser: UserTeamModel;

  // search model
  searchValue: any;

  // initialise ag grid
  gridApi;
  gridOptions = gridOptionsServerSide;
  columnsDefs;
  rowSelection = 'multiple';

  // users that are already added
  addedEntities = new Map<string, app_mst_link_subject_to_grp_RowClass>();
  // currently selected users
  selectedEntities = new Map<string, UserModel>();

  constructor(
    private linkSubjectToTeamService: LinkSubjectToTeamService,
    private tenantUserProfileService: TenantUserProfileService,
    private fb: FormBuilder,
    private viewColFacade: PermissionFacade,
    private readonly store: Store<PermissionStates>,
    private readonly componentStore: ComponentStore<{localState: LocalState}>
  ) {
    super();
  }

  /**
   * Lifecycle method: Called when the component is first initialised.
   */
   ngOnInit() {
    this.subSink.sink = this.localState$.subscribe(a => {
      this.localState = a;
      this.componentStore.setState({localState: a});
    });
    this.subSink.sink = this.viewColFacade.prevIndex$.subscribe(resolve => this.prevIndex = resolve);
    this.subSink.sink = this.viewColFacade.prevLocalState$().subscribe(resolve => this.prevLocalState = resolve);

    this.form = this.fb.group({
      currRank: [''],
      rank: ['', Validators.compose([Validators.required])]
    })

    this.newRankArr = this.rankArr;

    // define column definitions
    this.columnsDefs = [
      {
        headerName: '',
        field: 'app_login_subject_guid',
        valueFormatter: params => '',
        checkboxSelection: true,
        cellStyle: params => {
          return this.addedEntities.has(params.value) ?
          {'pointer-events': 'none', textAlign: 'left'} : {textAlign: 'left'}
        },
        floatingFilter: false,
        sortable: false,
        minWidth: 0,
      },
      {
        headerName: 'Email',
        field: 'app_login_principal_email.principal_id',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: 'Mobile',
        field: 'app_login_principal_mobile.principal_id',
        cellStyle: {textAlign: 'left'}
      }
    ];

    const currentUser$ = this.store.select(TeamPermSelectors.selectCurrentUser);
    const teamGuid$ = this.store.select(TeamPermSelectors.selectGuid);

    this.subSink.sink = zip(currentUser$, teamGuid$).subscribe(
      ([user, teamGuid]) => {
        this.currentUser = user;
        this.teamGuid = teamGuid;
        // set the available rank array based on the rank of the current user
        this.setRankArray(user.link_subject_to_grps
          .filter(x => x.app_grp_guid.toString() == this.teamGuid)[0].rank.toString())

        // get all current members
        this.subSink.sink = this.getMembers().subscribe(x => {
          // add members
          x.forEach(a => {
            this.addedEntities.set(a.link_subject_to_grp.app_subject_guid.toString(), a.link_subject_to_grp)
          })
        });
      }
    )
  }

  /**
   * Set the rank array based on the rank of the current user.
   *
   * @param currentUserRank : the rank of the current user
   */
  setRankArray(currentUserRank) {
    if (currentUserRank == 'OWNER') {
      this.rankArr = ['OWNER', 'MANAGER', 'ADMIN', 'MEMBER', 'GUEST', 'VISITOR', 'ANONYMOUS'];
    } else if (currentUserRank == 'MANAGER') {
      this.rankArr = ['MANAGER', 'ADMIN', 'MEMBER', 'GUEST', 'VISITOR', 'ANONYMOUS'];
    } else if (currentUserRank == 'ADMIN') {
      this.rankArr = ['ADMIN', 'MEMBER', 'GUEST', 'VISITOR', 'ANONYMOUS'];
    } else if (currentUserRank == 'MEMBER') {
      this.rankArr = ['MEMBER', 'GUEST', 'VISITOR', 'ANONYMOUS'];
    } else if (currentUserRank == 'GUEST') {
      this.rankArr = ['GUEST', 'VISITOR', 'ANONYMOUS'];
    } else if (currentUserRank == 'VISITOR') {
      this.rankArr = ['VISITOR', 'ANONYMOUS'];
    } else {
      this.rankArr = [];
    }
    this.newRankArr = this.rankArr;
  }

  /**
   * Function called when AG grid is initialised.
   *
   * @param params : grid parameters
   */
   onGridReady(params) {
    this.gridApi = params.api;
    this.gridApi.closeToolPanel();
    const datasource = this.getDatasource();
    this.gridApi.setServerSideDatasource(datasource);

    const allColIds = params.columnApi.getAllColumns().map(x => x.colId);
    params.columnApi.autoSizeColumns(allColIds);

    this.subSink.sink = this.store.select(TeamPermSelectors.selectUpdateAddMemberListing)
      .subscribe(a => {
        if (a) {
          // get all current members
          this.subSink.sink = this.getMembers().subscribe(x => {
            this.addedEntities.clear();
            // add members
            x.forEach(a => {
              this.addedEntities.set(a.link_subject_to_grp.app_subject_guid.toString(), a.link_subject_to_grp)
            });
            this.gridApi.refreshServerSideStore(
              { purge: true }
            )
            this.store.dispatch(TeamPermActions.setUpdateAddMemberListing({update: false}))
          });
        }
      })
  }

  /**
   * Sets the datasource on the AG grid to response data returned from the
   * getByCriteria API.
   *
   * @param conditionalCriteria : any custom criteria to search for in the API
   * @returns datasource for AG grid
   */
  getDatasource(conditionalCriteria?) {

    let offset = 0;
    let limit = this.paginationComponent.rowPerPage;

    let criteria = [
      {columnName: 'calcTotalRecords', operator: '=', value: 'true'},
      {columnName: 'principalStatus', operator: '=', value: 'USER_CONFIRMED'}
    ];

    if (conditionalCriteria) {
      criteria = criteria.concat(conditionalCriteria);
    }

    return {

      getRows: grid => {

        const sortModel = grid.request.sortModel;
        const filterModel = grid.request.filterModel;
        const sortOn = pageSorting(sortModel);
        const filter = pageFiltering(filterModel);

        // only change offset and limit if no filterModel or sortModel is present
        if (Object.keys(filterModel).length == 0 && sortModel.length == 0) {
          offset = grid.request.startRow;
          limit = grid.request.endRow - offset;
        }

        const pagination = new Pagination(offset, limit, criteria);

        this.subSink.sink = this.tenantUserProfileService.getByCriteria(pagination, this.apiVisa)
          .pipe(

            map((x: PagingResponseModel<AppLoginContainerModelUpdate>) => {

              const a = x.data;
              const users: UserModel[] = [];

              a
              .filter(x => x.appLoginPrincipalList.length >= 0)
              .map(x => x.appLoginPrincipalList)
              .forEach((b) => {

                const hasMobile = b.map(x => x.principal_type).includes("MOBILE_USERNAME") && b.filter(x => x.principal_type == "MOBILE_USERNAME")[0].status == 'USER_CONFIRMED';
                const hasEmail = b.map(x => x.principal_type).includes("EMAIL_USERNAME") && b.filter(x => x.principal_type == "EMAIL_USERNAME")[0].status == 'USER_CONFIRMED';

                const newModel: UserModel = new UserModel();
                newModel.app_login_subject_guid = b[0].subject_guid.toString();
                newModel.app_login_principal_mobile = hasMobile ? b.filter(x => x.principal_type == "MOBILE_USERNAME")[0] : null;
                newModel.app_login_principal_email = hasEmail ? b.filter(x => x.principal_type == "EMAIL_USERNAME")[0] : null;

                if (hasMobile || hasEmail) {
                  users.push(newModel);
                }

              });

              return {
                ...x,
                data: users
              }
            })
          ).subscribe(a => {
            // put data into the grid
            const data = sortOn(a.data).filter(entity => filter.by(entity));
            const totalRecords = filter.isFiltering ? a.totalRecords : data.length;
            // force pagination to go to first page if filter is applied
            if (this.paginationComponent.currentPage > this.paginationComponent.totalPage.value) { this.paginationComponent.firstPage() }

            grid.successCallback(data, totalRecords);
            this.setSelectedNodes();
          }, err => {
            grid.failCallback();
          })
      }
    }
  }

  /**
   * Returns an observable containing all the members of this team.
   *
   * @returns an observable containing all the members of this team
   */
  getMembers(): Observable<LinkSubjectToTeamContainerModel[]> {
    const criteria = [
      {columnName: 'calcTotalRecords', operator: '=', value: 'true'},
      {columnName: 'app_grp_guid', operator: '=', value: this.teamGuid}
    ];
    const pagination = new Pagination(null, null, criteria);

    return this.linkSubjectToTeamService.getByCriteria(pagination, this.apiVisa)
    .pipe(
      map(x => x.data)
    )
  }

  /**
   * This function is called whe the user clicks on the SEND INVITE button.
   * This will dispatch an NgRx action to update details.
   *
   */
   onSubmit() {
     const memberGuids = [...this.selectedEntities.keys()];
     // clear selected
     this.selectedEntities.clear();
     // info
     this.store.dispatch(TeamPermActions.addMembersInit({teamGuid: this.teamGuid, members: memberGuids, rank: this.form.value.rank}));
     this.formDisabled = true;
  }

  /**
   * This function is to be called every time the grid refreshes.
   * Set nodes that are already in the added to the team or previously currently
   * selected to selected.
   *
   */
  setSelectedNodes() {
    this.gridApi.forEachNode(node => {
      const guid = node.data.app_login_subject_guid;
      const inTeam = this.addedEntities.has(guid);
      const isCurrentlySelected = this.selectedEntities.has(guid);
      if (inTeam || isCurrentlySelected) {
        node.setSelected(true);
      } else {
        node.setSelected(false);
      }
    })
  }

  /**
   * This function is called when a row is either selected or deselected.
   * Updated the currentlySelected array.
   */
  onRowSelected(event) {
    const selectedGuid = event.node.data.app_login_subject_guid;
    const isSelectionEvent = event.node.isSelected();
    const inTeam = this.addedEntities.has(selectedGuid);
    const isCurrentlySelected = this.selectedEntities.has(selectedGuid);

    if (!isSelectionEvent && isCurrentlySelected && !inTeam) {
      // delete from selected entities if node is not already in team,
      // it is a deselection event and the node is in the currently selected entities
      this.selectedEntities.delete(selectedGuid)
    } else if (isSelectionEvent && !inTeam && !isCurrentlySelected) {
      // add node to the currently selected entities if it is a selection event,
      // the node is not currently already in the team and the node is
      // not in the currently selected maps already
      this.selectedEntities.set(selectedGuid, event.node.data)
    }

    if (this.selectedEntities.size > 0) {
      this.formDisabled = false;
    } else {
      this.formDisabled = true;
    }
  }

  /**
   * Dynamically change the options of shown on the screen by applying this filter.
   *
   * @param filterValue the value that the user types onto the screen
   */
   applyRankFilter(filterValue: string) {
    filterValue = filterValue.trim(); // remove whitespace
    filterValue = filterValue.toLowerCase();  // MatTableDataSource defaults to lowercase matches
    this.newRankArr = this.rankArr.filter( (option) =>
    option.toLowerCase().includes(filterValue) );
  }

  /**
   * Reset the search bar and search value.
   */
   reset() {
    this.searchValue = '';
    const datasource = this.getDatasource();
    this.gridApi.setServerSideDatasource(datasource);
  };

  /**
   * This function is called every time the user searches something in the
   * search bar.
   *
   */
   /**
   * Runs the search bar's keyword against the listing's own endpoint.
   *
   * Replaces the raw SELECT that went through SubQueryService:
   *
   *   FROM app_login_principal hdr
   *   WHERE hdr.principal_id ILIKE '%x%' AND hdr.status != 'DELETED'
   *
   * GET user-profile/query -> AppLoginUow.getByCriteriaForTenantUserProfile ->
   * AppLoginUowHelper.generateWhereClause applies `search_word` through
   * generateSearchWordCondition as a bound ILIKE over the joined
   * principal.principal_id plus the subject's main_email, main_phone, name,
   * name_first and name_last, and defaults to `status != 'DELETED'`. The
   * `principalStatus` scoping stays in this component's default criteria, which
   * getDatasource concatenates with what is passed in.
   *
   * Behaviour note: the keyword now also matches the subject's email, phone and
   * name, not just the principal id.
   */
  onSearch() {
    const datasource = this.getDatasource(this.buildSearchCriteria());
    this.gridApi.setServerSideDatasource(datasource);
  }

  /**
   * Criteria for the current keyword, or undefined when the box is empty so the
   * listing falls back to its default criteria.
   */
  buildSearchCriteria() {
    const keyword = (this.searchValue || '').toString().trim();
    if (!keyword) {
      return undefined;
    }
    return [
      { columnName: 'search_word', operator: '=', value: keyword }
    ];
  }

  /**
   * Return to the previous drawer/view column.
   */
   onReturn() {
    this.viewColFacade.updateInstance(this.prevIndex, {
      ...this.prevLocalState,
      deactivateReturn: false,
      deactivateAdd: false,
      deactivateList: false
    });
    this.viewColFacade.onPrev(this.prevIndex);
  }

  /**
   * Lifecycle method: Called when this component is destroyed.
   */
  ngOnDestroy() {
    if (this.matTab) {
      this.viewColFacade.updateInstance(this.index, {
        ...this.localState,
        selectedIndex: this.matTab.selectedIndex
      });
    }
    this.subSink.unsubscribe();
  }

}
